import { z } from 'zod';
import {
  EVALUATION_INFERENCE_PROVIDER_IDS,
  EVALUATION_REASONING_EFFORTS,
  EVALUATION_PLAYBOOK_OVERRIDE_MAX_CATEGORIES,
  EVALUATION_PLAYBOOK_OVERRIDE_MAX_CHARS,
  EVALUATION_PLAYBOOK_PHASES,
  findContradictoryPlaybookPhases,
} from '@terra/api-common-constants';
import { readRecord } from './utils/schema-validation.utils';

export function getRunScenarioInput(
  scenarioConfig: Record<string, unknown>,
): Record<string, unknown> | null {
  return readRecord(scenarioConfig.input);
}

export const EvaluationInferenceProviderIdSchema = z.enum(
  EVALUATION_INFERENCE_PROVIDER_IDS,
);

export type EvaluationInferenceProviderId = z.infer<
  typeof EvaluationInferenceProviderIdSchema
>;

export const EvaluationSlotPatchSchema = z.strictObject({
  model: z.string().min(1).optional(),
  providerId: EvaluationInferenceProviderIdSchema.optional(),
  temperature: z.number().min(0).max(2).optional(),
  reasoningEffort: z.enum(EVALUATION_REASONING_EFFORTS).optional(),
  systemPrompt: z.string().min(1).max(50_000).optional(),
  tools: z.array(z.string().min(1)).max(200).optional(),
});

export const EvaluationPlaybookPhaseSchema = z.enum(EVALUATION_PLAYBOOK_PHASES);

export const EvaluationPlaybookPhasesFilterSchema = z
  .strictObject({
    include: z.array(EvaluationPlaybookPhaseSchema).optional(),
    exclude: z.array(EvaluationPlaybookPhaseSchema).min(1).optional(),
  })
  .superRefine((filter, ctx) => {
    if (filter.include === undefined && filter.exclude === undefined) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Select an include or exclude list, or remove the playbook override to keep every playbook enabled',
      });
    }

    const contradictory = findContradictoryPlaybookPhases(filter);

    if (contradictory.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `These playbooks are both included and excluded: ${contradictory.join(', ')}`,
      });
    }
  });

export type EvaluationPlaybookPhase = z.infer<
  typeof EvaluationPlaybookPhaseSchema
>;
export type EvaluationPlaybookPhasesFilter = z.infer<
  typeof EvaluationPlaybookPhasesFilterSchema
>;

export const EvaluationPlaybookOverridesSchema = z
  .record(
    z.string().min(1),
    z.string().trim().min(1).max(EVALUATION_PLAYBOOK_OVERRIDE_MAX_CHARS),
  )
  .superRefine((overrides, ctx) => {
    const count = Object.keys(overrides).length;

    if (count > EVALUATION_PLAYBOOK_OVERRIDE_MAX_CATEGORIES) {
      ctx.addIssue({
        code: 'custom',
        message: `playbookOverrides must have at most ${EVALUATION_PLAYBOOK_OVERRIDE_MAX_CATEGORIES} keys (got ${count})`,
      });
    }
  });

export type EvaluationPlaybookOverrides = z.infer<
  typeof EvaluationPlaybookOverridesSchema
>;

export const EvaluationRunStatusSchema = z.enum([
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'ABORTED',
  'CANCELLED',
  'ABORTED',
]);

export const EvaluationRunKindSchema = z.enum(['RUN', 'SUITE', 'BATCH']);

export type EvaluationSlotPatch = z.infer<typeof EvaluationSlotPatchSchema>;
